import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Badge,
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  FiltersBar,
  Icon,
  IconButton,
  Input,
  Modal,
  SearchInput,
  Segmented,
  StatePanel,
  Textarea,
} from "../../components/ui";
import { createIncident, fetchIncident, fetchIncidents } from "./api.js";

/**
 * Incident log — the incident memory, readable (Alex, 2026-10-14).
 *
 * Nothing here needs feeding by hand. An incident is captured automatically
 * when an error or a beta report is resolved, when Claude's auto-fix reaches
 * staging, and nightly from merged fix pull requests. Every night Claude
 * writes each one up — what broke, why, the fix, and what to be careful about
 * — and files real breakages in the backend repo's knowledge/incidents/, the
 * folder developers and coding agents read before changing that area.
 */

const REPO_BLOB = "https://github.com/Retail-Circle/retail-circle.com/blob/master/";
const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "fixed", label: "Fixed" },
  { value: "needs_fix", label: "Needs a fix" },
];
const SOURCE_LABEL = {
  error_group: "Error",
  beta_report: "Beta report",
  pull_request: "Fix pull request",
  manual: "Logged by hand",
};

const day = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");

/** The workflow's cron is "15 2 * * *" — UTC. */
const nextWriteUp = () => {
  const now = new Date();
  const next = new Date(now);
  next.setUTCHours(2, 15, 0, 0);
  if (next <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const lessonPoints = (lesson) =>
  String(lesson || "")
    .split("\n")
    .map((l) => l.replace(/^\s*[-*]\s*/, "").trim())
    .filter(Boolean);

const StatusBadge = ({ incident }) =>
  incident.status === "needs_fix" ? (
    <Badge tone="warning" size="sm" dot>Needs a fix</Badge>
  ) : (
    <Badge tone="success" size="sm" dot>Fixed</Badge>
  );

/** Coloured, line-numbered-free diff. Long diffs stop with a link to the full write-up. */
const DiffView = ({ diff, fullUrl }) => {
  const MAX_LINES = 1500;
  const lines = diff.split("\n");
  return (
    <pre className="mon-incident__diff">
      {lines.slice(0, MAX_LINES).map((line, i) => {
        const kind = line.startsWith("+++") || line.startsWith("---") || line.startsWith("#")
          ? "file"
          : line.startsWith("@@") ? "hunk" : line.startsWith("+") ? "add" : line.startsWith("-") ? "del" : "ctx";
        return (
          <span key={i} className={`mon-incident__diff-${kind}`}>{line}{"\n"}</span>
        );
      })}
      {lines.length > MAX_LINES && (
        <span className="mon-incident__diff-file">
          … {lines.length - MAX_LINES} more lines{fullUrl ? " — open the full write-up on GitHub" : ""}
        </span>
      )}
    </pre>
  );
};

const IncidentDetail = ({ id, onClose }) => {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    fetchIncident(id)
      .then((data) => !cancelled && setState({ incident: data.incident }))
      .catch(() => !cancelled && setState({ error: true }));
    return () => { cancelled = true; };
  }, [id]);

  const incident = state.incident;
  const fileUrl = incident?.file_path ? REPO_BLOB + incident.file_path : null;
  const prUrl = incident?.evidence?.pull_request;

  return (
    <Card as="aside" surface="raised" padding="lg" className="mon-page__detail mon-incident">
      <div className="mon-page__detail-head">
        {incident && <StatusBadge incident={incident} />}
        {incident?.regression && <Badge tone="danger" size="sm">Regression</Badge>}
        <IconButton label="Close" variant="ghost" onClick={onClose}>
          <Icon name="X" size="md" />
        </IconButton>
      </div>

      {state.loading ? (
        <StatePanel state="loading" compact title="Loading…" description={null} />
      ) : state.error ? (
        <StatePanel state="error" compact title="Couldn't load this incident." description={null} />
      ) : (
        <>
          <h2 className="mon-page__detail-title">{incident.title}</h2>
          <div className="mon-page__detail-meta">
            <span>{day(incident.created_at)}</span>
            {incident.area_label && <span>· {incident.area_label}</span>}
            <span>· {SOURCE_LABEL[incident.source_type] || incident.source_type}{incident.source_id ? ` #${incident.source_id}` : ""}</span>
          </div>

          {!incident.written ? (
            <p className="mon-incident__pending">
              Claude writes this one up tonight (about {nextWriteUp()}): what broke, why, the fix and what to be
              careful about. The evidence is already saved.
            </p>
          ) : (
            <>
              <section className="mon-incident__section">
                <h3>What broke</h3>
                <p>{incident.what_broke}</p>
              </section>
              {incident.root_cause && (
                <section className="mon-incident__section">
                  <h3>Root cause</h3>
                  <p>{incident.root_cause}</p>
                </section>
              )}
              {incident.fix_summary && (
                <section className="mon-incident__section">
                  <h3>The fix</h3>
                  <p>{incident.fix_summary}</p>
                </section>
              )}
              {lessonPoints(incident.lesson).length > 0 && (
                <section className="mon-incident__lesson">
                  <h3>Be careful next time</h3>
                  <ul>
                    {lessonPoints(incident.lesson).map((point) => <li key={point}>{point}</li>)}
                  </ul>
                </section>
              )}
              {incident.is_breakage === false && (
                <p className="mon-incident__pending">Claude judged this not to be a real breakage, so it is not filed in the repo.</p>
              )}
            </>
          )}

          <div className="mon-incident__links">
            {fileUrl && <a href={fileUrl} target="_blank" rel="noreferrer noopener">Open the write-up on GitHub →</a>}
            {prUrl && <a href={prUrl} target="_blank" rel="noreferrer noopener">Open the pull request →</a>}
            {(incident.fix_refs || []).filter((r) => r.url && r.url !== prUrl).map((r) => (
              <a key={r.url} href={r.url} target="_blank" rel="noreferrer noopener">
                {r.kind === "pull" ? `Fix PR #${r.ref}` : `Fix commit ${r.ref.slice(0, 8)}`} →
              </a>
            ))}
          </div>

          {incident.diff_excerpt && (
            <details className="mon-incident__more">
              <summary>Code before and after</summary>
              <DiffView diff={incident.diff_excerpt} fullUrl={fileUrl} />
            </details>
          )}
          {incident.evidence && (
            <details className="mon-incident__more">
              <summary>Evidence as captured</summary>
              <pre className="mon-page__bundle">{JSON.stringify(incident.evidence, null, 2)}</pre>
            </details>
          )}
        </>
      )}
    </Card>
  );
};

/** Optional: for something that never reached the board. Everything else is automatic. */
const LogIncidentDialog = ({ open, onClose, onLogged }) => {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [links, setLinks] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await createIncident({ title, note: note || null, links: links || null });
      setTitle(""); setNote(""); setLinks("");
      onLogged();
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't log it. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log an incident"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={title.trim().length < 3}>Log incident</Button>
        </>
      )}
    >
      <div className="mon-incident__form">
        <p className="mon-incident__hint">
          Only for something that never reached the board — errors, beta reports and fix pull requests are
          logged automatically. Claude writes it up tonight.
        </p>
        <Field label="What broke" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Stripe links stopped reserving the watch" />
        </Field>
        <Field label="What happened" hint="Optional — anything that helps explain it">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} minRows={3} />
        </Field>
        <Field label="Fix pull request or commit links" hint="Optional — paste GitHub links; the code before and after is pulled in automatically">
          <Textarea value={links} onChange={(e) => setLinks(e.target.value)} minRows={2} />
        </Field>
        {error && <p className="mon-incident__error">{error}</p>}
      </div>
    </Modal>
  );
};

const IncidentLog = () => {
  const [area, setArea] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [logging, setLogging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page };
      if (area) params.area = area;
      if (status) params.status = status;
      if (debouncedQ) params.q = debouncedQ;
      setData(await fetchIncidents(params));
    } catch (e) {
      setError(e?.response?.status === 403 ? "The incident log is restricted to master admins." : "Couldn't load the incident log.");
    } finally {
      setLoading(false);
    }
  }, [area, status, debouncedQ, page]);

  useEffect(() => { setPage(1); }, [area, status, debouncedQ]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.incidents?.data || [];
  const pager = data?.incidents;

  const columns = useMemo(() => [
    // Phone cards: the title gets the full width; area + date share the subtitle line.
    { key: "date", header: "Date", cardHidden: true, cell: (r) => day(r.created_at) },
    {
      key: "card_meta",
      header: "Area and date",
      tableHidden: true,
      card: "subtitle",
      cell: (r) => `${r.area_label || "Written up tonight"} · ${day(r.created_at)}`,
    },
    {
      key: "title",
      header: "What broke",
      card: "title",
      cell: (r) => (
        <span className="mon-incident__title-cell">
          {r.title}
          {r.regression && <Badge tone="danger" size="sm">Regression</Badge>}
        </span>
      ),
    },
    {
      key: "area",
      header: "Area",
      cardHidden: true,
      cell: (r) => (r.area_label || (r.pending_write_up ? <span className="mon-incident__muted">Written up tonight</span> : "—")),
    },
    {
      key: "lesson",
      header: "Be careful",
      cardLabel: "Be careful",
      cell: (r) => <span className="mon-incident__lesson-cell">{lessonPoints(r.lesson)[0] || "—"}</span>,
    },
    { key: "status", header: "Status", card: "aside", cell: (r) => <StatusBadge incident={r} /> },
  ], []);

  const tableState = loading ? "loading" : error ? "error" : rows.length === 0 ? "no-results" : undefined;

  return (
    <>
      <div className="mon-incident__intro">
        <p>
          What broke, why, how it was fixed, and what to be careful about. Logged automatically when errors or beta
          reports are resolved and when fix pull requests merge; Claude writes each one up every night and files real
          breakages in the repo&apos;s <code>knowledge/incidents/</code> for future development.
        </p>
        <Button variant="secondary" size="sm" onClick={() => setLogging(true)}>Log an incident</Button>
      </div>

      {data?.areas && (
        <div className="mon-incident__areas" role="group" aria-label="Filter by feature area">
          <Chip selected={area === ""} onClick={() => setArea("")}>All areas</Chip>
          {data.areas.filter((a) => a.count > 0 || a.slug === area).map((a) => (
            <Chip key={a.slug} selected={area === a.slug} count={a.count} onClick={() => setArea(area === a.slug ? "" : a.slug)}>
              {a.label}
            </Chip>
          ))}
        </div>
      )}

      <FiltersBar
        label="Filter incidents"
        search={<SearchInput value={q} onChange={(e) => setQ(e.target.value)} onSearch={setDebouncedQ} placeholder="Search what broke or the lesson…" />}
      >
        <Segmented options={STATUS_FILTERS} value={status} onChange={setStatus} label="Filter by status" />
      </FiltersBar>

      <div className={`mon-page__body ${selectedId ? "mon-page__body--split" : ""}`}>
        <div className="mon-page__list">
          <DataTable
            caption="Incidents"
            columns={columns}
            rows={tableState ? [] : rows}
            rowKey="id"
            state={tableState}
            stateProps={
              error
                ? { title: error, description: null, onRetry: load }
                : { title: "No incidents yet", description: "They appear here automatically as errors and beta reports are resolved and fixes merge." }
            }
            onRowClick={(row) => setSelectedId(row.id)}
            rowActionLabel="Open incident"
            rowProps={(row) => (row.id === selectedId ? { className: "mon-page__row--active" } : {})}
          />
          {!tableState && pager?.last_page > 1 && (
            <nav className="mon-page__pager" aria-label="Incident pages">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="mon-page__pager-info">Page {pager.current_page} of {pager.last_page} · {pager.total} incidents</span>
              <Button variant="secondary" size="sm" disabled={page >= pager.last_page} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </nav>
          )}
        </div>

        {selectedId && <IncidentDetail key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} />}
      </div>

      <LogIncidentDialog open={logging} onClose={() => setLogging(false)} onLogged={() => { setLogging(false); load(); }} />
    </>
  );
};

export default IncidentLog;
